import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { STATUS_LABEL, type PruefErgebnis, type PruefStatus, type QualitativerHinweis } from "./pruefregeln";

const STATUS_COLOR: Record<PruefStatus, { bg: string; fg: string; dot: string }> = {
  passend: { bg: "#e6f2ea", fg: "#1d5c3c", dot: BRAND.pillarGreen },
  pruefen: { bg: "#fdf6e6", fg: "#7a5a10", dot: BRAND.gold },
  luecke: { bg: "#fbecec", fg: "#8f2b2b", dot: "#c23b3b" },
  offen: { bg: "#f3f0f5", fg: "#6b5a66", dot: "#c9c1cf" },
};

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "16px" },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
    "@media (min-width: 720px)": { gridTemplateColumns: "repeat(4, minmax(0, 1fr))" },
  },
  kpi: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("10px"),
  },
  kpiValue: { fontFamily: BRAND.fontAccent, fontSize: "1.6rem", lineHeight: 1 },
  kpiLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  table: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    overflowX: "auto",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(160px, 1.1fr) minmax(200px, 1.6fr) 130px",
    columnGap: "12px",
    alignItems: "start",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
    ":last-child": { ...shorthands.borderBottom("0") },
  },
  headRow: {
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
  },
  pruefung: { fontSize: "0.9rem", fontWeight: 700, color: BRAND.ink },
  sollIst: { fontSize: "0.74rem", color: BRAND.muted, marginTop: "3px" },
  detail: { fontSize: "0.86rem", color: BRAND.ink },
  logik: { fontSize: "0.76rem", color: BRAND.muted, marginTop: "4px" },
  hinweis: {
    fontSize: "0.76rem",
    color: "#7a5a10",
    marginTop: "6px",
    ...shorthands.borderLeft("2px", "solid", BRAND.gold),
    paddingLeft: "8px",
  },
  badge: {
    justifySelf: "start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.76rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "12px",
    ...shorthands.borderRadius("999px"),
  },
  dot: { width: "8px", height: "8px", ...shorthands.borderRadius("999px") },
  qualiBox: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  qualiTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
  },
  qualiItem: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    backgroundColor: "#fdf6e6",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
  },
  qualiItemTitle: { fontSize: "0.86rem", fontWeight: 700, color: "#7a5a10" },
  qualiItemText: { fontSize: "0.82rem", color: "#7a5a10" },
  qualiEmpty: {
    fontSize: "0.85rem",
    color: BRAND.muted,
    backgroundColor: "#faf8fb",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
  },
});

const StatusBadge = ({ status }: { status: PruefStatus }) => {
  const styles = useStyles();
  const c = STATUS_COLOR[status];
  return (
    <span className={styles.badge} style={{ backgroundColor: c.bg, color: c.fg }}>
      <span className={styles.dot} style={{ backgroundColor: c.dot }} />
      {STATUS_LABEL[status]}
    </span>
  );
};

type Props = {
  ergebnisse: PruefErgebnis[];
  hinweise: QualitativerHinweis[];
};

/**
 * Abschnitt 8 der Felddefinition, gerechnet nach dem Blatt „Soll-Ist-Abgleich“:
 * je Prüfung Soll, Ist, Prüflogik und Ergebnis — ergänzt um die qualitativen
 * Punkte aus Spalte E der Vertragsmaske.
 */
const AbgleichTabelle = ({ ergebnisse, hinweise }: Props) => {
  const styles = useStyles();
  const count = (s: PruefStatus) => ergebnisse.filter((e) => e.status === s).length;

  return (
    <div className={styles.root}>
      <div className={styles.summary}>
        {(["passend", "pruefen", "luecke", "offen"] as PruefStatus[]).map((s) => {
          const c = STATUS_COLOR[s];
          return (
            <div key={s} className={styles.kpi} style={{ backgroundColor: c.bg, color: c.fg }}>
              <span className={styles.kpiValue}>{count(s)}</span>
              <span className={styles.kpiLabel}>{STATUS_LABEL[s]}</span>
            </div>
          );
        })}
      </div>

      <div className={styles.table}>
        <div className={mergeClasses(styles.row, styles.headRow)}>
          <span>Prüfung</span>
          <span>Soll ↔ Ist</span>
          <span>Ergebnis</span>
        </div>
        {ergebnisse.map((e) => (
          <div key={e.key} className={styles.row}>
            <div>
              <div className={styles.pruefung}>{e.pruefung}</div>
              <div className={styles.sollIst}>
                Soll: {e.sollLabel}
                <br />
                Ist: {e.istLabel}
              </div>
            </div>
            <div>
              <div className={styles.detail}>{e.detail}</div>
              <div className={styles.logik}>{e.logik}</div>
              {e.hinweis && <div className={styles.hinweis}>{e.hinweis}</div>}
            </div>
            <StatusBadge status={e.status} />
          </div>
        ))}
      </div>

      <div className={styles.qualiBox}>
        <span className={styles.qualiTitle}>Qualitative Prüfpunkte</span>
        {hinweise.length ? (
          hinweise.map((h) => (
            <div key={h.key} className={styles.qualiItem}>
              <span className={styles.qualiItemTitle}>{h.titel}</span>
              <span className={styles.qualiItemText}>{h.text}</span>
            </div>
          ))
        ) : (
          <span className={styles.qualiEmpty}>
            Keine offenen qualitativen Punkte — Gefahrenstatus, Ausschlüsse, Sublimits und
            Nachweis sind erfasst.
          </span>
        )}
      </div>
    </div>
  );
};

export default AbgleichTabelle;
